import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Book, Lightbulb, Target, Award, Users, 
  TrendingUp, Sparkles, CheckCircle, Play, FileText,
  Brain, Zap, Globe, Heart
} from 'lucide-react';
import AppFooter from '../components/AppFooter';

const UserGuide = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = [
    { id: 'getting-started', icon: Sparkles, title: 'Getting Started' },
    { id: 'think-lab', icon: Brain, title: 'Think Lab Guide' },
    { id: 'evaluate-paper', icon: FileText, title: 'Evaluate Paper' },
    { id: 'impact-lab', icon: Target, title: 'Impact Lab Guide' },
    { id: 'scoring', icon: TrendingUp, title: 'Scoring Systems' },
    { id: 'challenges', icon: Globe, title: 'Challenges' },
    { id: 'teams', icon: Users, title: 'Teams & Collaboration' },
    { id: 'community', icon: Heart, title: 'Community Features' }
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              className="gap-2"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
            <div className="text-center">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                User Guide
              </h1>
              <p className="text-sm text-slate-600">Learn how to make the most of Idea Fountain</p>
            </div>
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8 max-w-7xl">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Book className="w-5 h-5" />
                  Contents
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                        activeSection === section.id
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                          : 'hover:bg-slate-100 text-slate-700'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-medium">{section.title}</span>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Getting Started */}
            <section id="getting-started">
              <Card className="border-2 border-purple-200">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-2xl">Getting Started</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-3">Welcome to Idea Fountain! 🌟</h3>
                    <p className="text-slate-700 leading-relaxed mb-4">
                      Idea Fountain is your Originality Enabling System (OES) that helps you transform ideas into measurable, 
                      ethically-grounded innovations using AI-powered structured thinking.
                    </p>
                  </div>

                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6">
                    <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-purple-600" />
                      Quick Start Steps:
                    </h4>
                    <ol className="space-y-3">
                      <li className="flex items-start gap-3">
                        <Badge className="bg-purple-600 mt-1">1</Badge>
                        <div>
                          <strong>Create Your Account</strong> - Sign up with email or Google
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Badge className="bg-purple-600 mt-1">2</Badge>
                        <div>
                          <strong>Start Your First Think Lab Session</strong> - Transform your idea through 6 structured stages
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Badge className="bg-purple-600 mt-1">3</Badge>
                        <div>
                          <strong>Get Your Scores</strong> - Receive OI, MSA, and IQI assessments
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Badge className="bg-purple-600 mt-1">4</Badge>
                        <div>
                          <strong>Explore Challenges</strong> - Join social innovation challenges aligned with UN SDGs
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Badge className="bg-purple-600 mt-1">5</Badge>
                        <div>
                          <strong>Build Your Community</strong> - Follow innovators, join teams, and collaborate
                        </div>
                      </li>
                    </ol>
                  </div>

                  <div className="flex gap-4">
                    <Button 
                      onClick={() => navigate('/session/new')}
                      className="bg-gradient-to-r from-purple-600 to-pink-600"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Start Your First Session
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => navigate('/challenges')}
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      Browse Challenges
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Think Lab Guide */}
            <section id="think-lab">
              <Card className="border-2 border-blue-200">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-2xl">Think Lab Guide</CardTitle>
                  </div>
                  <p className="text-slate-600">Your AI-Powered Ideation Studio</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-slate-700 leading-relaxed">
                    Think Lab is your personal AI-powered workspace for structured thinking. It combines conversational AI, 
                    resource management, and journaling to help you develop original ideas through a guided process.
                  </p>

                  {/* Key Features */}
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-6">
                    <h4 className="font-bold text-blue-900 mb-4 flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      Key Features of Think Lab:
                    </h4>
                    <div className="space-y-3">
                      <div className="bg-white rounded-lg p-4">
                        <h5 className="font-bold text-blue-900 mb-2">💬 AI Chat Interface</h5>
                        <p className="text-sm text-slate-700 mb-2">
                          Engage in real-time conversations with AI personas (Philosopher, Innovator, Pragmatist) 
                          who guide you through each stage with thoughtful questions and insights.
                        </p>
                        <ul className="text-xs text-slate-600 space-y-1 ml-4">
                          <li>• Switch personas for different perspectives</li>
                          <li>• AI adapts to your thinking style</li>
                          <li>• Get instant feedback on your ideas</li>
                        </ul>
                      </div>

                      <div className="bg-white rounded-lg p-4">
                        <h5 className="font-bold text-cyan-900 mb-2">📚 Resource Library</h5>
                        <p className="text-sm text-slate-700 mb-2">
                          Access AI-curated research, case studies, and precedents relevant to your challenge. 
                          Save resources for later reference.
                        </p>
                        <ul className="text-xs text-slate-600 space-y-1 ml-4">
                          <li>• Auto-generated research materials</li>
                          <li>• Save and organize resources</li>
                          <li>• Add your own references</li>
                        </ul>
                      </div>

                      <div className="bg-white rounded-lg p-4">
                        <h5 className="font-bold text-indigo-900 mb-2">🗂️ Think Projects</h5>
                        <p className="text-sm text-slate-700 mb-2">
                          Organize multiple ideas simultaneously. Each project tracks your progress through 
                          the 6 stages independently.
                        </p>
                        <ul className="text-xs text-slate-600 space-y-1 ml-4">
                          <li>• Multiple projects at once</li>
                          <li>• Track progress per stage</li>
                          <li>• Compare different approaches</li>
                        </ul>
                      </div>

                      <div className="bg-white rounded-lg p-4">
                        <h5 className="font-bold text-purple-900 mb-2">📝 Journal & Notes</h5>
                        <p className="text-sm text-slate-700 mb-2">
                          Capture insights, reflections, and eureka moments as you work. Your journal 
                          becomes part of your final analysis.
                        </p>
                        <ul className="text-xs text-slate-600 space-y-1 ml-4">
                          <li>• Auto-save all conversations</li>
                          <li>• Add private notes and reflections</li>
                          <li>• Export journal entries</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* How to Use */}
                  <div className="space-y-4">
                    <h4 className="font-bold text-slate-900 text-lg">How to Use Think Lab:</h4>
                    
                    <div className="bg-blue-50 rounded-lg p-5">
                      <h5 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                        <Badge className="bg-blue-600">Step 1</Badge>
                        Start a New Session
                      </h5>
                      <p className="text-sm text-slate-700 mb-3">
                        Click "New IF Session" and describe your challenge or idea. The AI will help you 
                        articulate your starting point.
                      </p>
                      <div className="bg-white rounded p-3 text-xs text-slate-600 border-l-4 border-blue-400">
                        <strong>Pro Tip:</strong> Don't worry about perfection - just start with what you know. 
                        The AI will help you clarify as you go.
                      </div>
                    </div>

                    <div className="bg-cyan-50 rounded-lg p-5">
                      <h5 className="font-bold text-cyan-900 mb-3 flex items-center gap-2">
                        <Badge className="bg-cyan-600">Step 2</Badge>
                        Choose Your AI Persona
                      </h5>
                      <p className="text-sm text-slate-700 mb-3">
                        Select the thinking partner that matches your current needs:
                      </p>
                      <ul className="text-sm text-slate-700 space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-cyan-700">🧠 Philosopher:</span>
                          <span>Deep questioning, ethical considerations, conceptual clarity</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-cyan-700">💡 Innovator:</span>
                          <span>Creative brainstorming, unconventional approaches, possibility thinking</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-cyan-700">⚙️ Pragmatist:</span>
                          <span>Practical execution, feasibility analysis, actionable next steps</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-indigo-50 rounded-lg p-5">
                      <h5 className="font-bold text-indigo-900 mb-3 flex items-center gap-2">
                        <Badge className="bg-indigo-600">Step 3</Badge>
                        Navigate Through Stages
                      </h5>
                      <p className="text-sm text-slate-700 mb-3">
                        Work through each of the 6 stages (UNDERSTAND → OBSERVE → DEFINE → IDEATE → PROTOTYPE → TEST). 
                        The interface guides you with:
                      </p>
                      <ul className="text-sm text-slate-700 space-y-1 ml-4">
                        <li>• Stage-specific prompts and questions</li>
                        <li>• Progress indicators showing completion</li>
                        <li>• Ability to revisit and refine previous stages</li>
                        <li>• Auto-save so you can pause anytime</li>
                      </ul>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-5">
                      <h5 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                        <Badge className="bg-purple-600">Step 4</Badge>
                        Use the Chat Interface
                      </h5>
                      <p className="text-sm text-slate-700 mb-3">
                        Interact with AI naturally through chat:
                      </p>
                      <ul className="text-sm text-slate-700 space-y-2">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5" />
                          <span><strong>Ask questions:</strong> "What precedents exist for this?"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5" />
                          <span><strong>Get feedback:</strong> "Does this approach make sense?"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5" />
                          <span><strong>Request alternatives:</strong> "What other solutions could work?"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5" />
                          <span><strong>Clarify thinking:</strong> "Help me understand this better"</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-pink-50 rounded-lg p-5">
                      <h5 className="font-bold text-pink-900 mb-3 flex items-center gap-2">
                        <Badge className="bg-pink-600">Step 5</Badge>
                        Build Your Resource Library
                      </h5>
                      <p className="text-sm text-slate-700 mb-3">
                        As you progress, AI suggests relevant resources. You can:
                      </p>
                      <ul className="text-sm text-slate-700 space-y-1 ml-4">
                        <li>• Save AI-suggested research and case studies</li>
                        <li>• Add your own articles, papers, and references</li>
                        <li>• Tag resources by stage or topic</li>
                        <li>• Export your resource library for external use</li>
                      </ul>
                    </div>

                    <div className="bg-orange-50 rounded-lg p-5">
                      <h5 className="font-bold text-orange-900 mb-3 flex items-center gap-2">
                        <Badge className="bg-orange-600">Step 6</Badge>
                        Keep a Reflective Journal
                      </h5>
                      <p className="text-sm text-slate-700 mb-3">
                        Document your thinking journey:
                      </p>
                      <ul className="text-sm text-slate-700 space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600">→</span>
                          <span>Write down insights and breakthrough moments</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600">→</span>
                          <span>Track how your thinking evolves across stages</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600">→</span>
                          <span>Note questions that arise for later exploration</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600">→</span>
                          <span>All entries become part of your final OI/MSA assessment</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-slate-100 rounded-lg p-5">
                    <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-purple-600" />
                      Pro Tips for Think Lab:
                    </h4>
                    <ul className="space-y-2 text-sm text-slate-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Engage deeply:</strong> The more you interact with AI, the richer your analysis becomes</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Switch personas:</strong> Try different AI partners for varied perspectives</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Save everything:</strong> Sessions auto-save but manually save important insights</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Take breaks:</strong> Come back with fresh eyes - your session is always there</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Revisit stages:</strong> It's not linear - go back and refine as needed</span>
                      </li>
                    </ul>
                  </div>

                  <Button 
                    onClick={() => navigate('/session/new')}
                    className="bg-gradient-to-r from-blue-600 to-cyan-600 w-full"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Your First Think Lab Session
                  </Button>
                </CardContent>
              </Card>
            </section>

            {/* Evaluate Paper Section */}
            <section id="evaluate-paper">
              <Card className="border-2 border-emerald-200">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-2xl">Evaluate Research Papers</CardTitle>
                  </div>
                  <p className="text-slate-600">AI-Powered Academic Paper Assessment</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid lg:grid-cols-3 gap-6">
                    {/* Main Content - Left Side */}
                    <div className="lg:col-span-2 space-y-6">
                      <p className="text-slate-700 leading-relaxed">
                        Quick Evaluate analyzes your research papers instantly using GPT-5, providing detailed scores 
                        and actionable feedback to improve your academic work before submission.
                      </p>

                      {/* How to Use - Practical Steps */}
                      <div className="space-y-4">
                        <h4 className="font-bold text-slate-900 text-lg">How to Use Quick Evaluate:</h4>
                        
                        <div className="bg-emerald-50 rounded-lg p-5">
                          <h5 className="font-bold text-emerald-900 mb-3 flex items-center gap-2">
                            <Badge className="bg-emerald-600">Step 1</Badge>
                            Navigate to Quick Evaluate
                          </h5>
                          <p className="text-sm text-slate-700">
                            Click "Evaluate Paper" button in the homepage header or quick actions section
                          </p>
                        </div>

                        <div className="bg-teal-50 rounded-lg p-5">
                          <h5 className="font-bold text-teal-900 mb-3 flex items-center gap-2">
                            <Badge className="bg-teal-600">Step 2</Badge>
                            Choose Your Input Method
                          </h5>
                          <ul className="text-sm text-slate-700 space-y-2">
                            <li className="flex items-start gap-2">
                              <FileText className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                              <span><strong>Upload PDF:</strong> Click "Upload" and select your paper (up to 25MB)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <FileText className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                              <span><strong>Paste Text:</strong> Copy full paper text and paste into the text area</span>
                            </li>
                          </ul>
                          <p className="text-xs text-slate-600 mt-2 italic">
                            Tip: PDF upload is recommended for best formatting preservation
                          </p>
                        </div>

                        <div className="bg-cyan-50 rounded-lg p-5">
                          <h5 className="font-bold text-cyan-900 mb-3 flex items-center gap-2">
                            <Badge className="bg-cyan-600">Step 3</Badge>
                            Fill in Paper Details (Optional)
                          </h5>
                          <p className="text-sm text-slate-700 mb-2">Add context for more targeted feedback:</p>
                          <ul className="text-sm text-slate-700 space-y-1 ml-4">
                            <li>• Paper title</li>
                            <li>• Research field (e.g., AI, Biology, Economics)</li>
                            <li>• Target journal or conference</li>
                            <li>• Specific concerns or focus areas</li>
                          </ul>
                        </div>

                        <div className="bg-blue-50 rounded-lg p-5">
                          <h5 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                            <Badge className="bg-blue-600">Step 4</Badge>
                            Click "Evaluate" & Wait
                          </h5>
                          <p className="text-sm text-slate-700">
                            Processing takes 30-90 seconds depending on paper length. You'll see a progress indicator 
                            while GPT-5 analyzes your paper.
                          </p>
                        </div>

                        <div className="bg-indigo-50 rounded-lg p-5">
                          <h5 className="font-bold text-indigo-900 mb-3 flex items-center gap-2">
                            <Badge className="bg-indigo-600">Step 5</Badge>
                            Review Your Detailed Report
                          </h5>
                          <p className="text-sm text-slate-700 mb-2">
                            Your evaluation appears on screen with:
                          </p>
                          <ul className="text-sm text-slate-700 space-y-1">
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-indigo-600" />
                              Overall OI score (0-100)
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-indigo-600" />
                              4-dimension breakdown
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-indigo-600" />
                              Strengths & weaknesses
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-indigo-600" />
                              Actionable recommendations
                            </li>
                          </ul>
                        </div>

                        <div className="bg-purple-50 rounded-lg p-5">
                          <h5 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                            <Badge className="bg-purple-600">Step 6</Badge>
                            Download PDF Report
                          </h5>
                          <p className="text-sm text-slate-700">
                            Click "Download Report" button to save a professional PDF with all scores, feedback, and 
                            suggestions for your records or revision planning.
                          </p>
                        </div>
                      </div>

                      {/* Pro Tips */}
                      <div className="bg-slate-100 rounded-lg p-5">
                        <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                          <Lightbulb className="w-5 h-5 text-emerald-600" />
                          Pro Tips:
                        </h4>
                        <ul className="space-y-2 text-sm text-slate-700">
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span><strong>Complete papers:</strong> Full papers (with abstract, intro, methods, results, conclusion) get most accurate evaluations</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span><strong>Iterate:</strong> Revise based on feedback, then re-evaluate to track improvements</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span><strong>Before submission:</strong> Use 2-3 days before your deadline for time to revise</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span><strong>Privacy:</strong> Papers are analyzed securely and not stored after evaluation</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    {/* Sample Report Visual - Right Side */}
                    <div className="lg:col-span-1">
                      <div className="sticky top-24 space-y-4">
                        <h4 className="font-bold text-slate-900 text-sm">📊 Sample Evaluation Report</h4>
                        
                        {/* Sample Report Card */}
                        <Card className="border-2 border-emerald-300 shadow-lg">
                          <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white pb-3">
                            <div className="text-xs opacity-90">Evaluation Report</div>
                            <h5 className="font-bold text-sm">Research Paper Analysis</h5>
                          </CardHeader>
                          <CardContent className="pt-4 space-y-3">
                            {/* Overall Score */}
                            <div className="text-center pb-3 border-b">
                              <div className="text-3xl font-bold text-emerald-600">87</div>
                              <div className="text-xs text-slate-600">Overall OI Score</div>
                              <Badge className="bg-emerald-100 text-emerald-700 mt-2 text-xs">High Quality</Badge>
                            </div>

                            {/* Dimension Scores */}
                            <div className="space-y-2">
                              <div className="text-xs font-semibold text-slate-700 mb-2">4-Dimension Breakdown:</div>
                              
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-slate-600">Novelty</span>
                                <div className="flex items-center gap-2">
                                  <div className="w-16 h-1.5 bg-slate-200 rounded-full">
                                    <div className="w-[85%] h-full bg-purple-500 rounded-full"></div>
                                  </div>
                                  <span className="text-xs font-bold text-slate-700">85</span>
                                </div>
                              </div>

                              <div className="flex justify-between items-center">
                                <span className="text-xs text-slate-600">Resonance</span>
                                <div className="flex items-center gap-2">
                                  <div className="w-16 h-1.5 bg-slate-200 rounded-full">
                                    <div className="w-[90%] h-full bg-blue-500 rounded-full"></div>
                                  </div>
                                  <span className="text-xs font-bold text-slate-700">90</span>
                                </div>
                              </div>

                              <div className="flex justify-between items-center">
                                <span className="text-xs text-slate-600">Synthesis</span>
                                <div className="flex items-center gap-2">
                                  <div className="w-16 h-1.5 bg-slate-200 rounded-full">
                                    <div className="w-[82%] h-full bg-cyan-500 rounded-full"></div>
                                  </div>
                                  <span className="text-xs font-bold text-slate-700">82</span>
                                </div>
                              </div>

                              <div className="flex justify-between items-center">
                                <span className="text-xs text-slate-600">Ethical</span>
                                <div className="flex items-center gap-2">
                                  <div className="w-16 h-1.5 bg-slate-200 rounded-full">
                                    <div className="w-[91%] h-full bg-green-500 rounded-full"></div>
                                  </div>
                                  <span className="text-xs font-bold text-slate-700">91</span>
                                </div>
                              </div>
                            </div>

                            {/* Key Strengths */}
                            <div className="pt-2 border-t">
                              <div className="text-xs font-semibold text-green-700 mb-1">✓ Key Strengths:</div>
                              <ul className="text-xs text-slate-600 space-y-1">
                                <li>• Novel methodology</li>
                                <li>• Strong data analysis</li>
                                <li>• Clear conclusions</li>
                              </ul>
                            </div>

                            {/* Areas to Improve */}
                            <div className="pt-2 border-t">
                              <div className="text-xs font-semibold text-orange-700 mb-1">→ Improve:</div>
                              <ul className="text-xs text-slate-600 space-y-1">
                                <li>• Literature review depth</li>
                                <li>• Limitations discussion</li>
                              </ul>
                            </div>

                            {/* Action Button */}
                            <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700 text-xs mt-3">
                              <FileText className="w-3 h-3 mr-1" />
                              Download Full Report
                            </Button>
                          </CardContent>
                        </Card>

                        <div className="text-xs text-slate-500 italic text-center">
                          ↑ Sample report preview<br/>Your actual report will be more detailed
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Use Cases */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border-l-4 border-purple-600">
                    <h4 className="font-bold text-purple-900 mb-3">Perfect For:</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      <ul className="space-y-2 text-sm text-slate-700">
                        <li className="flex items-start gap-2">
                          <span className="text-purple-600">→</span>
                          <span><strong>PhD Students:</strong> Dissertation chapters</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-purple-600">→</span>
                          <span><strong>Researchers:</strong> Pre-submission check</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-purple-600">→</span>
                          <span><strong>Professors:</strong> Review student work</span>
                        </li>
                      </ul>
                      <ul className="space-y-2 text-sm text-slate-700">
                        <li className="flex items-start gap-2">
                          <span className="text-purple-600">→</span>
                          <span><strong>Grant Writers:</strong> Proposal quality</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-purple-600">→</span>
                          <span><strong>Peer Reviewers:</strong> Second opinion</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-purple-600">→</span>
                          <span><strong>Institutions:</strong> Quality assurance</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <Button 
                    onClick={() => navigate('/quick-evaluate')}
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 w-full"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Try Quick Evaluate Now
                  </Button>
                </CardContent>
              </Card>
            </section>

            {/* Impact Lab Guide */}
            <section id="impact-lab">
              <Card className="border-2 border-green-200">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-2xl">Impact Lab Guide</CardTitle>
                  </div>
                  <p className="text-slate-600">7-Day IDEO Design Thinking Sprint</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-slate-700 leading-relaxed">
                    Impact Lab applies IDEO's human-centered design methodology to social innovation challenges, 
                    powered by GPT-5 for deep research and analysis.
                  </p>

                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6">
                    <h4 className="font-bold text-green-900 mb-4">The 7-Day Process:</h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <Badge className="bg-green-600 flex-shrink-0">Day 1-2</Badge>
                        <div>
                          <strong className="text-green-900">EMPATHIZE</strong>
                          <p className="text-sm text-slate-700">Deep stakeholder research, personas, user stories, community pain points</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Badge className="bg-green-600 flex-shrink-0">Day 3</Badge>
                        <div>
                          <strong className="text-green-900">DEFINE</strong>
                          <p className="text-sm text-slate-700">Problem synthesis, scope definition, success metrics</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Badge className="bg-green-600 flex-shrink-0">Day 4</Badge>
                        <div>
                          <strong className="text-green-900">IDEATE</strong>
                          <p className="text-sm text-slate-700">10+ solution approaches, 5+ precedent analysis, resource compilation</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Badge className="bg-green-600 flex-shrink-0">Day 5</Badge>
                        <div>
                          <strong className="text-green-900">PROTOTYPE</strong>
                          <p className="text-sm text-slate-700">Concept refinement, implementation roadmap, Think Lab exercises</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Badge className="bg-green-600 flex-shrink-0">Day 6</Badge>
                        <div>
                          <strong className="text-green-900">TEST</strong>
                          <p className="text-sm text-slate-700">Validation strategies, risk assessment, stakeholder feedback plan</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Badge className="bg-green-600 flex-shrink-0">Day 7</Badge>
                        <div>
                          <strong className="text-green-900">CERTIFICATE</strong>
                          <p className="text-sm text-slate-700">Completion certificate, summary report, next steps</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button 
                    onClick={() => navigate('/challenges')}
                    className="bg-gradient-to-r from-green-600 to-emerald-600"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Start an Impact Lab
                  </Button>
                </CardContent>
              </Card>
            </section>

            {/* Scoring Systems */}
            <section id="scoring">
              <Card className="border-2 border-orange-200">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-2xl">Scoring Systems</CardTitle>
                  </div>
                  <p className="text-slate-600">Understanding OI, MSA, and IQI</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* OI */}
                  <div className="bg-purple-50 rounded-lg p-6">
                    <h4 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                      <Award className="w-5 h-5" />
                      Originality Index (OI)
                    </h4>
                    <p className="text-slate-700 mb-4">
                      Measures the originality of your idea across 4 dimensions (0-100 scale):
                    </p>
                    <ul className="space-y-2 text-sm text-slate-700">
                      <li className="flex items-start gap-2">
                        <span className="font-semibold text-purple-700 min-w-[120px]">Novelty:</span>
                        <span>How unique and unprecedented is your idea?</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-semibold text-purple-700 min-w-[120px]">Resonance:</span>
                        <span>Does it address real needs and resonate with stakeholders?</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-semibold text-purple-700 min-w-[120px]">Synthesis:</span>
                        <span>How well does it integrate existing concepts?</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-semibold text-purple-700 min-w-[120px]">Ethical Anchoring:</span>
                        <span>Is it grounded in moral and ethical considerations?</span>
                      </li>
                    </ul>
                    <p className="text-xs text-slate-600 mt-3 italic">Privacy: Opt-in (you control sharing)</p>
                  </div>

                  {/* MSA */}
                  <div className="bg-blue-50 rounded-lg p-6">
                    <h4 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                      <Award className="w-5 h-5" />
                      Mirror-Spark Assessment (MSA)
                    </h4>
                    <p className="text-slate-700 mb-4">
                      Dual scoring system evaluating your creative process:
                    </p>
                    <div className="space-y-3">
                      <div className="bg-white rounded p-4">
                        <strong className="text-blue-700">Mirror Score:</strong>
                        <p className="text-sm text-slate-700 mt-1">Reflects your self-awareness and depth of thinking</p>
                      </div>
                      <div className="bg-white rounded p-4">
                        <strong className="text-blue-700">Spark Score:</strong>
                        <p className="text-sm text-slate-700 mt-1">Measures your creative potential and innovation</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-600 mt-3 italic">Privacy: Opt-in (you control sharing)</p>
                  </div>

                  {/* IQI */}
                  <div className="bg-green-50 rounded-lg p-6">
                    <h4 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                      <Award className="w-5 h-5" />
                      Interaction Quality Index (IQI)
                    </h4>
                    <p className="text-slate-700 mb-4">
                      Measures the quality of your interactions and contributions across 5 dimensions:
                    </p>
                    <ul className="space-y-2 text-sm text-slate-700">
                      <li className="flex items-start gap-2">
                        <span className="font-semibold text-green-700 min-w-[120px]">Clarity:</span>
                        <span>How clear is your communication?</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-semibold text-green-700 min-w-[120px]">Depth:</span>
                        <span>Substance and insight of your contributions</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-semibold text-green-700 min-w-[120px]">Empathy:</span>
                        <span>Social awareness and consideration</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-semibold text-green-700 min-w-[120px]">Reciprocity:</span>
                        <span>Quality of collaboration and engagement</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-semibold text-green-700 min-w-[120px]">Transformative:</span>
                        <span>Potential for meaningful impact</span>
                      </li>
                    </ul>
                    <p className="text-xs text-slate-600 mt-3 italic">Privacy: Always Public (visible to community)</p>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Challenges */}
            <section id="challenges">
              <Card className="border-2 border-pink-200">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl flex items-center justify-center">
                      <Globe className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-2xl">Social Innovation Challenges</CardTitle>
                  </div>
                  <p className="text-slate-600">27+ Challenges Aligned with UN SDGs</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-700 leading-relaxed">
                    Join global challenges addressing real-world problems. Each challenge is aligned with UN Sustainable Development Goals (SDGs 4, 9, 12, 16).
                  </p>

                  <div className="bg-pink-50 rounded-lg p-5">
                    <h4 className="font-bold text-pink-900 mb-3">How to Participate:</h4>
                    <ol className="space-y-2 text-sm text-slate-700">
                      <li className="flex gap-3">
                        <span className="font-bold text-pink-600">1.</span>
                        Browse challenges and find one that resonates with you
                      </li>
                      <li className="flex gap-3">
                        <span className="font-bold text-pink-600">2.</span>
                        Read the challenge brief and understand the problem context
                      </li>
                      <li className="flex gap-3">
                        <span className="font-bold text-pink-600">3.</span>
                        Create an Impact Lab (7-day sprint) or submit a Think Lab session
                      </li>
                      <li className="flex gap-3">
                        <span className="font-bold text-pink-600">4.</span>
                        Receive feedback from the community and earn recognition
                      </li>
                    </ol>
                  </div>

                  <Button 
                    onClick={() => navigate('/challenges')}
                    className="bg-gradient-to-r from-pink-600 to-rose-600"
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    Explore Challenges
                  </Button>
                </CardContent>
              </Card>
            </section>

            {/* Teams */}
            <section id="teams">
              <Card className="border-2 border-indigo-200">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-2xl">Teams & Collaboration</CardTitle>
                  </div>
                  <p className="text-slate-600">Collaborate with Other Innovators</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-700 leading-relaxed">
                    Create or join teams to collaborate on challenges, share Think Lab sessions, and build collective innovation capacity.
                  </p>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-indigo-50 rounded-lg p-5">
                      <h4 className="font-bold text-indigo-900 mb-2">Team Features:</h4>
                      <ul className="space-y-2 text-sm text-slate-700">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-indigo-600" />
                          Shared workspaces
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-indigo-600" />
                          Collaborative sessions
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-indigo-600" />
                          Team challenges
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-indigo-600" />
                          Shared Impact Labs
                        </li>
                      </ul>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-5">
                      <h4 className="font-bold text-purple-900 mb-2">Team Roles:</h4>
                      <ul className="space-y-2 text-sm text-slate-700">
                        <li className="flex items-center gap-2">
                          <Badge className="bg-purple-600 text-xs">Owner</Badge>
                          Full control & admin
                        </li>
                        <li className="flex items-center gap-2">
                          <Badge className="bg-purple-500 text-xs">Admin</Badge>
                          Manage members
                        </li>
                        <li className="flex items-center gap-2">
                          <Badge className="bg-purple-400 text-xs">Member</Badge>
                          Collaborate & contribute
                        </li>
                      </ul>
                    </div>
                  </div>

                  <Button 
                    onClick={() => navigate('/teams/create')}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Create a Team
                  </Button>
                </CardContent>
              </Card>
            </section>

            {/* Community */}
            <section id="community">
              <Card className="border-2 border-rose-200">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl flex items-center justify-center">
                      <Heart className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-2xl">Community Features</CardTitle>
                  </div>
                  <p className="text-slate-600">Fountainhead Community</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-700 leading-relaxed">
                    Connect with fellow innovators, build your reputation, and contribute to a community focused on responsible innovation.
                  </p>

                  <div className="space-y-4">
                    <div className="bg-rose-50 rounded-lg p-5">
                      <h4 className="font-bold text-rose-900 mb-3">Community Features:</h4>
                      <ul className="space-y-2 text-sm text-slate-700">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-rose-600" />
                          <strong>Follow System:</strong> Follow innovators you admire
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-rose-600" />
                          <strong>Leaderboards:</strong> IQI, OI, and MSA rankings
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-rose-600" />
                          <strong>Public Profiles:</strong> Showcase your work and scores
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-rose-600" />
                          <strong>Discussion Boards:</strong> Engage in challenge discussions
                        </li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-5 border-l-4 border-purple-600">
                      <p className="text-sm text-slate-700 italic">
                        <strong className="text-purple-900">"Inspired by Ayn Rand's The Fountainhead"</strong> - 
                        Every person is a fountainhead of original ideas. Our community celebrates individual creativity 
                        while fostering collaboration for collective impact.
                      </p>
                    </div>
                  </div>

                  <Button 
                    onClick={() => navigate('/community')}
                    className="bg-gradient-to-r from-rose-600 to-pink-600"
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Explore Community
                  </Button>
                </CardContent>
              </Card>
            </section>

            {/* Need Help */}
            <Card className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
              <CardContent className="pt-6">
                <h3 className="text-2xl font-bold mb-3">Still Have Questions?</h3>
                <p className="mb-6 opacity-90">
                  We're here to help! Reach out to our support team or explore our community forums.
                </p>
                <div className="flex gap-4">
                  <Button 
                    variant="outline"
                    className="border-white text-white hover:bg-white/10"
                    onClick={() => window.location.href = 'mailto:hey@ideafountain.studio'}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Contact Support
                  </Button>
                  <Button 
                    variant="outline"
                    className="border-white text-white hover:bg-white/10"
                    onClick={() => navigate('/challenges')}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Community Forums
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <AppFooter className="bg-gradient-to-r from-purple-100 to-pink-100 border-t-slate-300" />
    </div>
  );
};

export default UserGuide;
